import { useTranslation } from "react-i18next";
import { RayMeter } from "@/components/brand/RayMeter";
import { WhatsAppButton } from "@/components/ui/Cta";
import { formatEuro, type PricingContent, type PricingPack } from "@/data/pricing";
import type { AppLocale } from "@/i18n/constants";

const POSTS: Record<string, number> = { esencia: 6, crecimiento: 8, evolucion: 12 };

function planWhatsAppMessage(pack: PricingPack, locale: AppLocale) {
  return locale === "en"
    ? `Hi Jenifer, I'm interested in the ${pack.name} plan.`
    : `Hola Jenifer, me interesa el plan ${pack.name}.`;
}

export function PlanCard({
  pack,
  pricing,
  postsLabel,
  foundationLabel,
  detailed = false,
  delay = 0,
}: {
  pack: PricingPack;
  pricing: PricingContent;
  postsLabel: string;
  foundationLabel: string;
  detailed?: boolean;
  delay?: number;
}) {
  const { i18n } = useTranslation();
  const locale = (i18n.language === "en" ? "en" : "es") as AppLocale;
  const featured = Boolean(pack.featured);
  const posts = POSTS[pack.id];
  const foundation = pack.cadence === "once";
  const cta = locale === "en" ? `I want ${pack.name}` : `Quiero ${pack.name}`;

  const card = (
    <article
      id={pack.id}
      aria-labelledby={`plan-${pack.id}`}
      className={`relative flex h-full flex-col rounded-[1.75rem] p-7 transition-[translate,box-shadow] duration-500 ease-[var(--ease-out-expo)] motion-safe:hover:-translate-y-1.5 hover:shadow-lit-lg sm:p-8 ${
        featured
          ? "on-navy bg-brand-primary text-white shadow-lit-lg ring-1 ring-brand-secondary/70"
          : "border border-brand-primary/10 bg-white shadow-lit"
      }`}
    >
      {featured ? (
        <p className="absolute -top-3.5 left-7 rounded-full bg-brand-secondary px-3.5 py-1 text-xs font-semibold text-brand-primary">
          {pricing.featuredLabel}
        </p>
      ) : null}

      <div className="flex items-center gap-4">
        <RayMeter lit={posts ?? 0} foundation={foundation} tone={featured ? "navy" : "light"} className="size-16 shrink-0" />
        <div>
          <h3 id={`plan-${pack.id}`} className="font-display text-2xl font-semibold leading-tight">
            {pack.name}
          </h3>
          <p className={`text-sm ${featured ? "text-teal-light" : "text-teal-strong"}`}>{pack.goal}</p>
        </div>
      </div>

      <p className="mt-7 flex items-baseline gap-2">
        <span className="font-display text-5xl font-semibold tracking-[-0.02em]">{formatEuro(pack.price, locale)}</span>
        <span className={`text-sm ${featured ? "text-on-navy-muted" : "text-ink-muted"}`}>
          {pack.cadence === "month" ? pricing.perMonth : pricing.oneOff}
        </span>
      </p>
      <p className={`mt-1 text-sm font-medium ${featured ? "text-on-navy-muted" : "text-ink-muted"}`}>
        {foundation ? foundationLabel : `${posts} ${postsLabel}`}
      </p>

      <p className={`mt-5 leading-relaxed ${featured ? "text-on-navy-muted" : "text-ink-soft"}`}>{pack.idealFor}</p>

      <ul className="mt-6 space-y-2.5 text-base">
        {pack.includes.map((item) => (
          <li key={item} className="node">
            {item}
          </li>
        ))}
      </ul>

      {detailed && pack.outcomes.length > 0 ? (
        <div className={`mt-6 border-t pt-5 ${featured ? "border-white/15" : "border-brand-primary/10"}`}>
          <ul className="flex flex-wrap gap-2">
            {pack.outcomes.map((outcome) => (
              <li
                key={outcome}
                className={`rounded-full px-3 py-1 text-xs font-medium ${featured ? "bg-white/10 text-white" : "bg-teal-soft text-teal-strong"}`}
              >
                {outcome}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {detailed && pack.note ? (
        <p className={`mt-5 text-sm leading-relaxed ${featured ? "text-on-navy-muted" : "text-ink-muted"}`}>{pack.note}</p>
      ) : null}

      <div className="mt-auto pt-8">
        <WhatsAppButton
          location={`plan_${pack.id}`}
          variant={featured ? "teal" : "outline"}
          size="md"
          className="w-full"
          label={cta}
          message={planWhatsAppMessage(pack, locale)}
        />
      </div>
    </article>
  );

  const reveal = { "data-reveal": "", style: { ["--reveal-delay" as string]: `${delay}ms` } };

  if (!featured) {
    return (
      <div {...reveal} className="h-full">
        {card}
      </div>
    );
  }

  /* The recommended plan glows from behind instead of rising above the row. */
  return (
    <div {...reveal} className="group/plan relative isolate h-full">
      <span
        aria-hidden="true"
        className="plan-glow pointer-events-none absolute -inset-4 -z-10 rounded-[2.5rem] transition-[translate] duration-500 ease-[var(--ease-out-expo)] motion-safe:group-hover/plan:-translate-y-1.5"
      />
      {card}
    </div>
  );
}
